/**
 * SmartSuggestInput — Iter 213
 *
 * A reusable free-text input with contextual suggestions from an existing
 * ERP dataset (customers, suppliers, products, materials, projects…).
 *
 * Core contract (per system-wide input-UX directive):
 *   • Field is ALWAYS a normal editable text input.
 *   • User can type anything; suggestions never force a match.
 *   • Selecting a suggestion links {selectedId, value}.
 *   • Manually editing after a selection clears the stale ID.
 *   • Turkish characters are normalized for search (İ/ı/I → i, Ş→s, …).
 *   • Ranking priority: exact prefix > word-start > contains.
 *   • Keyboard: Arrow Up/Down, Enter, Escape, Tab.
 *   • Debounced (200 ms) local filtering — no request per keystroke.
 *   • Component is presentational; caller supplies the dataset via `items`.
 *
 * Usage:
 *   <SmartSuggestInput
 *     label="Müşteri *"
 *     value={form.customer_name}
 *     selectedId={form.customer_id}
 *     items={customers}
 *     displayField="legal_name"
 *     idField="id"
 *     secondaryField={(c) => `${c.city || ""} · Kod: ${c.code || ""}`}
 *     placeholder="Müşteri adı yazın veya listeden seçin..."
 *     onChange={({ value, selectedId, item }) =>
 *       setForm(prev => ({
 *         ...prev,
 *         customer_name: value,
 *         customer_id: selectedId,
 *         customer_code: item?.code || "",
 *       }))
 *     }
 *     data-testid="so-customer-smart"
 *   />
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";

const TR_MAP = { "İ": "i", "I": "i", "ı": "i", "Ş": "s", "ş": "s",
                 "Ğ": "g", "ğ": "g", "Ü": "u", "ü": "u",
                 "Ö": "o", "ö": "o", "Ç": "c", "ç": "c" };

function normalizeTR(s) {
  if (!s) return "";
  return String(s).split("").map(ch => TR_MAP[ch] ?? ch).join("").toLowerCase();
}

function rank(query, text) {
  const q = normalizeTR(query);
  const t = normalizeTR(text);
  if (!q) return 0;
  if (t === q) return 1000;
  if (t.startsWith(q)) return 800;
  // word-start
  const words = t.split(/\s+/);
  if (words.some(w => w.startsWith(q))) return 600;
  if (t.includes(q)) return 400;
  return 0;
}

export default function SmartSuggestInput({
  label,
  value = "",
  selectedId = null,
  items = [],
  displayField = "name",
  idField = "id",
  secondaryField = null,      // (item) => "..." | string field name
  placeholder = "Yazın veya listeden seçin...",
  disabled = false,
  required = false,
  onChange,                    // ({ value, selectedId, item }) => void
  onBlur,
  maxSuggestions = 8,
  minChars = 1,
  debounceMs = 200,
  // Iter217 — Smart Linked Fields:
  priorityFilter = null,       // (item) => boolean; matching items ranked +200
  priorityLabel = "İlgili",
  helperText = null,           // small caption below the input (e.g. "MP10482 projesinden geldi")
  emptyAction = null,          // { label, onClick(text) } — Iter218 promote-to-master CTA
  // Iter269 — secondary searchable field(s): search matches BOTH the primary
  // displayField and this extra text (or list). Ex: SO Proje No aramasında
  // hem project_no hem product_name eşleşsin diye kullanılır.
  searchAccessor = null,       // (item) => string | string[]
  "data-testid": testId,
}) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [debouncedValue, setDebouncedValue] = useState(value);
  const wrapperRef = useRef(null);

  // Debounce filtering — the input itself updates instantly (controlled).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), debounceMs);
    return () => clearTimeout(t);
  }, [value, debounceMs]);

  const suggestions = useMemo(() => {
    if (!debouncedValue || debouncedValue.length < minChars) return [];
    const scored = items
      .map(it => {
        let score = rank(debouncedValue, it[displayField] || "");
        // Iter269 — also match secondary searchable text(s). Lower weight
        // than a primary hit but still visible in the list.
        if (searchAccessor) {
          const extras = searchAccessor(it);
          const list = Array.isArray(extras) ? extras : [extras];
          for (const t of list) {
            if (!t) continue;
            const s2 = rank(debouncedValue, t);
            if (s2 > 0) score = Math.max(score, Math.round(s2 * 0.85));
          }
        }
        if (score > 0 && priorityFilter && priorityFilter(it)) score += 200;
        return { it, score, priority: !!(priorityFilter && priorityFilter(it)) };
      })
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, maxSuggestions);
    return scored;
  }, [debouncedValue, items, displayField, minChars, maxSuggestions, priorityFilter, searchAccessor]);

  // Click-away — close list but keep typed value.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const setTyped = (v) => {
    // If user edits away from the currently linked record's exact label,
    // clear the stale selectedId.
    let nextId = selectedId;
    if (selectedId) {
      const linked = items.find(it => it[idField] === selectedId);
      if (!linked || (linked[displayField] || "") !== v) nextId = null;
    }
    onChange?.({ value: v, selectedId: nextId, item: null });
    setOpen(true);
    setHi(0);
  };

  const pick = (item) => {
    onChange?.({
      value: item[displayField] || "",
      selectedId: item[idField],
      item,
    });
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length > 0) {
        setOpen(true); e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") { setHi((hi + 1) % suggestions.length); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setHi((hi - 1 + suggestions.length) % suggestions.length); e.preventDefault(); }
    else if (e.key === "Enter") {
      if (hi >= 0 && hi < suggestions.length) {
        pick(suggestions[hi].it || suggestions[hi]);
        e.preventDefault();
      }
    }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const showSecondary = (item) => {
    if (!secondaryField) return null;
    if (typeof secondaryField === "function") return secondaryField(item);
    return item[secondaryField] || null;
  };

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="block text-xs font-semibold text-slate-500 mb-1">
          {label}{required && !value && <span className="text-rose-500 ml-1">*</span>}
        </label>
      )}
      <div className="flex items-center gap-2 px-2.5 py-1.5 border border-slate-300 rounded bg-white focus-within:ring-2 focus-within:ring-indigo-400">
        <Search size={13} className="text-slate-400 shrink-0"/>
        <input
          type="text"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => setTyped(e.target.value)}
          onFocus={() => value && setOpen(true)}
          onBlur={() => setTimeout(() => { onBlur?.(); }, 100)}
          onKeyDown={onKeyDown}
          className="flex-1 text-sm outline-none bg-transparent"
          data-testid={testId}
        />
        {selectedId && (
          <span title="Kayıtlı müşteri/kayıt bağlandı" className="text-emerald-600 text-[10px] font-semibold shrink-0">✓ BAĞLI</span>
        )}
        {value && !disabled && (
          <button type="button" onClick={() => setTyped("")} title="Temizle" className="text-slate-400 hover:text-slate-700 shrink-0">
            <X size={13}/>
          </button>
        )}
      </div>
      {open && (suggestions.length > 0 || emptyAction) && (
        <div className="absolute z-40 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg"
             data-testid={testId ? `${testId}-suggest` : undefined}>
          {suggestions.map((entry, idx) => {
            const item = entry.it || entry;
            const isPriority = !!entry.priority;
            return (
              <button
                type="button"
                key={item[idField] || idx}
                onMouseDown={(e) => { e.preventDefault(); pick(item); }}
                onMouseEnter={() => setHi(idx)}
                className={`w-full text-left px-3 py-1.5 text-xs border-b last:border-b-0 ${idx === hi ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                data-testid={testId ? `${testId}-hit-${item[idField]}` : undefined}
              >
                <div className="flex items-center gap-1.5">
                  {isPriority && (
                    <span className="text-[9px] font-semibold rounded bg-emerald-100 text-emerald-700 px-1 py-0.5">
                      {priorityLabel}
                    </span>
                  )}
                  <div className="font-semibold text-slate-800">{item[displayField]}</div>
                </div>
                {showSecondary(item) && (
                  <div className="text-[10px] text-slate-500 mt-0.5">{showSecondary(item)}</div>
                )}
              </button>
            );
          })}
          {/* Iter218: Optional "promote to master" CTA. Shows when user typed a
              value with no exact-match suggestion. Never auto-triggers. */}
          {emptyAction && (() => {
            const exact = suggestions.find(s => {
              const it = s.it || s;
              return normalizeTR(it[displayField] || "") === normalizeTR(debouncedValue);
            });
            if (exact) return null;
            const trimmed = (debouncedValue || "").trim();
            if (!trimmed) return null;
            return (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); emptyAction.onClick?.(trimmed); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold border-t border-emerald-200"
                data-testid={testId ? `${testId}-promote` : undefined}
              >
                + {emptyAction.label || `"${trimmed}" değerini yeni kayıt olarak ekle`}
              </button>
            );
          })()}
        </div>
      )}
      {helperText && (
        <div className="mt-1 text-[10px] text-emerald-700 flex items-center gap-1" data-testid={testId ? `${testId}-helper` : undefined}>
          {helperText}
        </div>
      )}
    </div>
  );
}
